import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container, Card } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css'
import { LuGraduationCap, LuUsers, LuWallet } from 'react-icons/lu'
import axios from 'axios'
import Cookies from 'js-cookie'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'

const thStyle = {
  fontSize: '13px',
  fontWeight: '600',
  padding: '16px',
  border: 'none',
  borderBottom: '1px solid #D1D3D4',
  textAlign: 'left',
  color: '#111827',
  fontFamily: 'Inter, system-ui, sans-serif',
}

const tdStyle = {
  fontSize: '14px',
  padding: '16px',
  border: 'none',
  color: '#374151',
  fontFamily: 'Inter, system-ui, sans-serif',
  textAlign: 'left',
}

const ParentDashboard = () => {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()
  const navigate = useNavigate()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 992 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState({
    total_students: 0,
    total_pending_fees_count: 0,
    total_pending_fees_amount: 0,
    students: [],
  })

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992
      setIsMobile(mobile)
      if (!mobile) {
        // 1px under the sidebar border so header/page always meets it (no white seam)
        setMarginLeft(isCollapsed ? '84px' : '244px')
      } else {
        setMarginLeft('0px')
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true)
        const userCookie = Cookies.get('user')
        if (!userCookie) {
          navigate('/LoginSignup', { replace: true })
          return
        }

        const parsedUser = JSON.parse(userCookie)
        const userType = String(parsedUser?.user_type || '').toLowerCase()
        if (userType !== 'parents' && userType !== 'parent') {
          navigate('/Dashboard', { replace: true })
          return
        }

        const token = parsedUser?.token
        const instituteId = Cookies.get('institute_id')

        const headers = {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        }
        if (instituteId) {
          headers['Institute-Id'] = Number(instituteId)
        }

        const response = await axios.get(BASE_URL + '/parents/dashboard', {
          headers,
        })

        if (response.data?.status && response.data?.data) {
          setDashboardData({
            total_students: response.data.data.total_students || 0,
            total_pending_fees_count:
              response.data.data.total_pending_fees_count || 0,
            total_pending_fees_amount:
              response.data.data.total_pending_fees_amount || 0,
            students: response.data.data.students || [],
          })
        }
      } catch (error) {
        console.log(error.response?.data?.message || error.message)
      } finally {
        setLoading(false)
      }
    }

    fetchDashboard()
  }, [navigate])

  const formatCurrency = (amount) =>
    `₹${Number(amount || 0).toLocaleString('en-IN')}`

  const formatDate = (dateValue) => {
    if (!dateValue || dateValue === '...') return '—'
    const date = new Date(dateValue)
    if (Number.isNaN(date.getTime())) return dateValue
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  // Flatten each pending fee into its own table row
  const pendingFeeRows = (dashboardData.students || []).flatMap((student) =>
    (student.pending_fees || []).map((fee) => ({
      key: `${student.student_id}-${fee.id}`,
      student_id: student.student_id,
      student_name: student.student_name,
      admission_number: student.admission_number,
      course_name: student.course_name || fee.course_name,
      class_name: student.class_name,
      course_id: student.course_id,
      class_id: student.class_id,
      fees_name: fee.fees_name,
      payable_amount: fee.payable_amount ?? fee.amount,
      fees_status: fee.fees_status,
      days_overdue: fee.days_overdue,
      end_date: fee.end_date,
    })),
  )

  const studentCount = dashboardData.total_students || 0
  const totalPending = dashboardData.total_pending_fees_amount || 0

  return (
    <div
      className="parent-dashboard-page"
      style={{
        // Never apply sidebar offset on mobile — avoids side gaps
        marginLeft: isMobile ? 0 : marginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
        paddingLeft: 0,
        paddingRight: 0,
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        width: isMobile ? '100%' : undefined,
        maxWidth: isMobile ? '100%' : undefined,
        overflowX: isMobile ? 'hidden' : undefined,
      }}
    >
      <style>
        {`
          .parent-dashboard-page {
            background: #F3F4F6;
            min-height: 100vh;
            box-sizing: border-box;
          }
          .parent-dashboard-header-bleed {
            margin-left: 0;
            margin-right: 0;
          }
          .parent-dashboard-header {
            position: relative;
            background: #ffffff;
            border-bottom: 1px solid #D1D3D4;
            padding: 14px 28px;
            width: 100%;
            box-sizing: border-box;
            border-radius: 0;
          }
          /* Fill any sub-pixel seam between fixed sidebar and header */
          @media (min-width: 992px) {
            .parent-dashboard-header::before {
              content: '';
              position: absolute;
              top: 0;
              bottom: -1px;
              left: -4px;
              width: 4px;
              background: #ffffff;
              pointer-events: none;
            }
          }
          .parent-dashboard-body {
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
            overflow-x: hidden;
          }
          .parent-pending-fees-wrapper {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            overflow-x: auto;
            overflow-y: visible;
            -webkit-overflow-scrolling: touch;
          }
          .parent-pending-fees-wrapper .responsiveTable {
            width: 100%;
          }
          @media screen and (min-width: 40.01em) {
            .parent-pending-fees-wrapper .responsiveTable {
              min-width: 960px;
            }
            .parent-pending-fees-wrapper .responsiveTable th,
            .parent-pending-fees-wrapper .responsiveTable td {
              white-space: nowrap;
            }
          }
          .parent-stat-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px;
            width: 100%;
            min-width: 0;
          }
          .parent-stat {
            background: #ffffff;
            border: 1px solid #D1D3D4;
            border-radius: 12px;
            padding: 16px 16px 14px;
            display: flex;
            flex-direction: column;
            min-height: 148px;
            width: 100%;
            min-width: 0;
            text-align: left;
            box-shadow: 0 1px 2px rgba(34, 56, 67, 0.04);
            transition: transform 0.22s ease, box-shadow 0.22s ease;
          }
          .parent-stat:hover {
            transform: translateY(-6px);
            box-shadow: 0 12px 24px rgba(34, 56, 67, 0.12);
          }
          .parent-stat-top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 12px;
          }
          .parent-stat-label {
            margin: 0;
            font-size: 13px;
            font-weight: 600;
            color: #6b7280;
            font-family: Inter, system-ui, sans-serif;
          }
          .parent-stat-icon {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .parent-stat-value {
            margin: 10px 0 0;
            font-size: 28px;
            line-height: 1.1;
            font-weight: 700;
            color: #223843;
            font-family: Inter, system-ui, sans-serif;
            word-break: break-word;
          }
          .parent-stat-meta {
            margin: 8px 0 0;
            font-size: 12px;
            line-height: 1.4;
            color: #6b7280;
            font-family: Inter, system-ui, sans-serif;
          }
          .parent-stat-action {
            margin-top: auto;
            padding: 12px 0 0;
            background: none;
            border: none;
            font-size: 13px;
            font-weight: 600;
            font-family: Inter, system-ui, sans-serif;
            cursor: pointer;
            text-align: left;
          }
          .parent-stat-action:hover {
            text-decoration: underline;
          }
          .parent-fees-panel {
            background: #ffffff;
            border: 1px solid #D1D3D4;
            border-radius: 12px;
            box-shadow: 0 1px 2px rgba(34, 56, 67, 0.04);
            overflow: hidden;
            width: 100%;
            max-width: 100%;
            min-width: 0;
          }
          @media (max-width: 991.98px) {
            .parent-dashboard-page {
              width: 100% !important;
              max-width: 100vw !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              overflow-x: hidden !important;
            }
            /*
             * Full-bleed header on mobile: span the real viewport so no side
             * gaps show even if a parent is padded/constrained.
             * margin-top: sit flush under fixed top bar (cancel App 12px padding).
             */
            .app-main-content .parent-dashboard-header-bleed.container-fluid.p-0,
            .parent-dashboard-header-bleed {
              width: 100% !important;
              max-width: 100% !important;
              position: relative;
              left: auto;
              margin-left: 0 !important;
              margin-right: 0 !important;
              margin-top: calc(var(--mobile-topbar-height, 56px) + env(safe-area-inset-top, 0px) - 12px) !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              box-sizing: border-box;
            }
            .app-main-content .container-fluid.p-0 .parent-dashboard-header.d-flex,
            .parent-dashboard-header {
              width: 100% !important;
              max-width: 100% !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              border-radius: 0 !important;
              padding-left: 12px !important;
              padding-right: 12px !important;
              padding-top: 12px !important;
              padding-bottom: 12px !important;
            }
            .parent-stat-grid {
              grid-template-columns: 1fr;
            }
            .parent-stat-value {
              font-size: 24px;
            }
          }
          @media screen and (max-width: 40em) {
            .parent-pending-fees-wrapper {
              overflow-x: visible !important;
              overflow-y: visible !important;
            }
            .parent-pending-fees-card {
              overflow: visible !important;
            }
            .parent-pending-fees-wrapper .responsiveTable tbody tr {
              border: 1px solid #D1D3D4 !important;
              border-radius: 10px;
              margin-bottom: 12px;
              background: #ffffff;
              padding: 8px 4px;
            }
            .parent-pending-fees-wrapper .responsiveTable td.pivoted {
              white-space: normal !important;
              overflow: visible !important;
              word-break: break-word;
              color: #111827 !important;
              padding-top: 10px !important;
              padding-bottom: 10px !important;
            }
            .parent-pending-fees-wrapper .responsiveTable td .tdBefore {
              color: #6b7280;
              font-size: 12px;
            }
          }
        `}
      </style>
      {/* Header — flush under mobile top bar / flush to sidebar on desktop */}
      <div
        className="container-fluid p-0 parent-dashboard-header-bleed"
        style={{
          // Mobile margin-top is handled in CSS so it touches the top navbar
          marginTop: isMobile ? undefined : '-12px',
          paddingLeft: 0,
          paddingRight: 0,
        }}
      >
        <div
          className="parent-dashboard-header d-flex justify-content-between align-items-center"
          style={{
            marginLeft: 0,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          }}
        >
          <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                overflow: 'hidden',
                background: '#223843',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {!logoError ? (
                <img
                  src={logo}
                  alt="School Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={() => setLogoError(true)}
                />
              ) : (
                <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <h3
                className="m-0 fw-bold text-start"
                style={{
                  letterSpacing: '-0.02em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: isMobile ? '16px' : 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.35',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Parent Dashboard
              </h3>
              <p
                className="m-0 mt-1 text-start"
                style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: '#6b7280',
                  fontWeight: '400',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Overview of your linked students and pending fees.
              </p>
            </div>
          </div>

          {!isMobile && <ProfileDropdown logout={logout} />}
        </div>
      </div>

      <Container
        fluid
        className="py-4 parent-dashboard-body"
        style={{
          paddingLeft: isMobile ? '16px' : isCollapsed ? '28px' : '40px',
          paddingRight: isMobile ? '16px' : '28px',
        }}
      >
        <div className="parent-stat-grid mb-4">
          <article className="parent-stat">
            <div className="parent-stat-top">
              <p className="parent-stat-label">My Students</p>
              <div className="parent-stat-icon" style={{ background: '#14B8A618', color: '#14B8A6' }}>
                <LuUsers size={18} />
              </div>
            </div>
            <p className="parent-stat-value">{loading ? '—' : studentCount}</p>
            <p className="parent-stat-meta">
              {loading
                ? 'Loading linked students...'
                : `${studentCount} linked student${studentCount === 1 ? '' : 's'} under your account`}
            </p>
            <button
              type="button"
              className="parent-stat-action"
              style={{ color: '#14B8A6' }}
              onClick={() => navigate('/parent/student/list')}
            >
              View Students
            </button>
          </article>

          <article className="parent-stat">
            <div className="parent-stat-top">
              <p className="parent-stat-label">Pending Fees</p>
              <div
                className="parent-stat-icon"
                style={{
                  background: totalPending > 0 ? '#F59E0B18' : '#10B98118',
                  color: totalPending > 0 ? '#F59E0B' : '#10B981',
                }}
              >
                <LuWallet size={18} />
              </div>
            </div>
            <p className="parent-stat-value">{loading ? '—' : formatCurrency(totalPending)}</p>
            <p className="parent-stat-meta">
              {loading
                ? 'Loading pending fees...'
                : `${dashboardData.total_pending_fees_count || 0} outstanding fee(s)`}
            </p>
          </article>
        </div>

        {/* Pending fees table */}
        <h4
          className="mb-3"
          style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#223843',
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          Fees due for your children
        </h4>

        <Card className="parent-pending-fees-card parent-fees-panel">
          <div className="parent-pending-fees-wrapper">
            <Table className="table mb-0" style={{ borderRadius: '12px' }}>
              <Thead>
                <Tr style={{ backgroundColor: '#F3F4F6' }}>
                  <Th style={thStyle}>Student Name</Th>
                  <Th style={thStyle}>Admission No.</Th>
                  <Th style={thStyle}>Course</Th>
                  <Th style={thStyle}>Class</Th>
                  <Th style={thStyle}>Fee Name</Th>
                  <Th style={thStyle}>Due Date</Th>
                  <Th style={thStyle}>Overdue</Th>
                  <Th style={{ ...thStyle, textAlign: 'right' }}>Amount</Th>
                  <Th style={{ ...thStyle, textAlign: 'center' }}>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td
                      colSpan="9"
                      className="text-center"
                      style={{
                        ...tdStyle,
                        padding: '40px',
                        color: '#6b7280',
                        textAlign: 'center',
                      }}
                    >
                      Loading pending fees...
                    </Td>
                  </Tr>
                ) : pendingFeeRows.length > 0 ? (
                  pendingFeeRows.map((row, index) => (
                    <Tr
                      key={row.key}
                      style={{
                        backgroundColor:
                          index % 2 === 0
                            ? '#ffffff'
                            : 'rgba(243, 244, 246, 0.4)',
                      }}
                    >
                      <Td style={{ ...tdStyle, color: '#111827', fontWeight: '600' }}>
                        {row.student_name || '—'}
                      </Td>
                      <Td style={tdStyle}>{row.admission_number || '—'}</Td>
                      <Td style={tdStyle}>{row.course_name || '—'}</Td>
                      <Td style={tdStyle}>{row.class_name || '—'}</Td>
                      <Td style={tdStyle}>{row.fees_name || '—'}</Td>
                      <Td style={tdStyle}>{formatDate(row.end_date)}</Td>
                      <Td style={tdStyle}>
                        {Number(row.days_overdue) > 0 ? (
                          <span
                            style={{
                              backgroundColor: '#FEE2E2',
                              color: '#991B1B',
                              fontSize: '12px',
                              fontWeight: '600',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {row.days_overdue} day(s)
                          </span>
                        ) : (
                          <span
                            style={{
                              backgroundColor: '#FEF3C7',
                              color: '#92400E',
                              fontSize: '12px',
                              fontWeight: '600',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Due
                          </span>
                        )}
                      </Td>
                      <Td
                        style={{
                          ...tdStyle,
                          color: '#DC2626',
                          textAlign: 'right',
                          fontWeight: '700',
                        }}
                      >
                        {formatCurrency(row.payable_amount)}
                      </Td>
                      <Td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button
                          className="btn btn-sm"
                          onClick={() =>
                            navigate(
                              `/parent/student/fees/${row.student_id}/${row.course_id || '_'}/${row.class_id || '_'}`,
                            )
                          }
                          style={{
                            backgroundColor: '#223843',
                            color: '#ffffff',
                            borderRadius: '10px',
                            border: 'none',
                            padding: '8px 14px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            fontWeight: 600,
                          }}
                        >
                          View
                        </button>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td
                      colSpan="9"
                      className="text-center"
                      style={{
                        padding: '40px',
                        fontSize: '14px',
                        color: '#6b7280',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        border: 'none',
                      }}
                    >
                      <LuWallet
                        size={28}
                        style={{ color: '#10B981', marginBottom: 8 }}
                      />
                      <div>No pending fees for your students.</div>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </div>
        </Card>
      </Container>
    </div>
  )
}

export default ParentDashboard
